import {
  DiscountApplicationType,
  DiscountDiscountType,
  DiscountSettingFieldType,
  OfferDto,
  OfferTierUnitFieldType,
  VariationDto,
} from "@intelligems/ig-types";
import * as rtk from "@reduxjs/toolkit";
import { type PayloadAction, createSelector } from "@reduxjs/toolkit";

import { fromGqlId } from "../../api/formatters/shopify";
import { DISCOUNT_MAX_UNITS, initialOfferState } from "../../api/initials";
import {
  addTier,
  removeTier,
  setUnit,
  toggleDiscountUnitType,
} from "../../api/reducers/discounts/sharedDiscountReducerActions";
import { getSpecificRedirectsUtil } from "../../features/experienceTests/components/redirects/getSpecificRedirects";
import { getOfferIndexByVariationId } from "../helpers";
import { IOffersState } from "../types/offers";

const sliceInitialOffersState: IOffersState = {
  isSuccess: false,
  offers: [],
  storedOffers: [],
  campaignBeingEditedOffer: undefined,
};

const offersSlice = rtk.createSlice({
  name: "offers",
  initialState: sliceInitialOffersState,
  reducers: {
    resetOffer: (draft) => {
      draft = sliceInitialOffersState;
      return draft;
    },
    resetOfferToStored: (draft) => {
      draft = {
        ...draft,
        offers: [],
      };
      return draft;
    },
    addOfferTier: (
      draft,
      action: PayloadAction<{
        variationId: string;
        isCampaignBeingEdited: boolean;
      }>
    ) => {
      if (
        action.payload.isCampaignBeingEdited &&
        draft.campaignBeingEditedOffer
      ) {
        draft.campaignBeingEditedOffer = addTier(
          draft.campaignBeingEditedOffer
        );
      } else if (draft.offers) {
        const index = getOfferIndexByVariationId(
          draft.offers,
          action.payload.variationId
        );
        draft.offers[index] = addTier(draft.offers[index]);
      }
      return draft;
    },
    initPersonalizationOffer: (
      draft,
      action: PayloadAction<{
        variationId: string;
        discountApplicationType?: DiscountApplicationType;
      }>
    ) => {
      draft.isSuccess = true;
      draft.offers = [
        initialOfferState({
          variationId: action.payload.variationId,
          discountApplicationType: action.payload.discountApplicationType,
        }),
      ];

      return draft;
    },
    initOffersFromVariations: (
      draft,
      action: PayloadAction<{ variations: VariationDto[] }>
    ) => {
      const tempOffers: OfferDto[] = draft.offers;
      action.payload.variations.forEach((variation) => {
        if (variation.offer) {
          const tempOffersVariationIds: string[] = draft.offers.map(
            (offer) => offer.variationId
          );

          if (tempOffersVariationIds.includes(variation.id)) {
            const index = tempOffers.findIndex(
              (offer) => offer.variationId === variation.id
            );
            if (index > -1) {
              tempOffers[index] = variation.offer;
            }
          } else {
            tempOffers.push(variation.offer);
          }
        }
      });

      if (tempOffers.length) {
        draft.isSuccess = true;
        draft.offers = tempOffers;
        draft.storedOffers = tempOffers;
      }
      return draft;
    },
    initCampaignCreateOffer: (
      draft,
      action: PayloadAction<{ variationId: string }>
    ) => {
      draft.isSuccess = true;
      draft.campaignBeingEditedOffer = initialOfferState({
        variationId: action.payload.variationId,
      });

      return draft;
    },
    resetCampaignCreateOffer: (draft) => {
      draft.campaignBeingEditedOffer = undefined;
      return draft;
    },
    setCampaignBeingEditedOffer: (
      draft,
      action: PayloadAction<{ offer: OfferDto }>
    ) => {
      draft.isSuccess = true;
      draft.campaignBeingEditedOffer = action.payload.offer;

      return draft;
    },
    removeOfferTier: (
      draft,
      action: PayloadAction<{
        tierIndex: number;
        variationId: string;
        isCampaignBeingEdited: boolean;
      }>
    ) => {
      if (
        action.payload.isCampaignBeingEdited &&
        draft.campaignBeingEditedOffer
      ) {
        draft.campaignBeingEditedOffer = removeTier(
          draft.campaignBeingEditedOffer,
          action.payload.tierIndex
        );
      } else if (draft.offers) {
        const index = getOfferIndexByVariationId(
          draft.offers,
          action.payload.variationId
        );
        draft.offers[index] = removeTier(
          draft.offers[index],
          action.payload.tierIndex
        );
      }
      return draft;
    },
    switchToCartDiscountType: (
      draft,
      action: PayloadAction<{
        variationId: string;
        isCampaignBeingEdited: boolean;
      }>
    ) => {
      if (
        action.payload.isCampaignBeingEdited &&
        draft.campaignBeingEditedOffer
      ) {
        draft.campaignBeingEditedOffer.tiers = [
          {
            ...draft.campaignBeingEditedOffer.tiers[0],
            unitDiscount: 0,
            discountTitle: null,
            isFreeShipping: false,
            isGiftWithPurchase: false,
            maximumUnits: DISCOUNT_MAX_UNITS,
          },
        ];
      } else if (draft.offers) {
        const index = getOfferIndexByVariationId(
          draft.offers,
          action.payload.variationId
        );

        draft.offers[index].tiers = [
          {
            ...draft.offers[index].tiers[0],
            unitDiscount: 0,
            discountTitle: null,
            isFreeShipping: false,
            isGiftWithPurchase: false,
            maximumUnits: DISCOUNT_MAX_UNITS,
          },
        ];
      }
      return draft;
    },
    setOfferUnit: (
      draft,
      action: PayloadAction<{
        tierIndex: number;
        key: OfferTierUnitFieldType;
        value: number;
        variationId: string;
        isCampaignBeingEdited: boolean;
      }>
    ) => {
      if (
        action.payload.isCampaignBeingEdited &&
        draft.campaignBeingEditedOffer
      ) {
        draft.campaignBeingEditedOffer = setUnit(
          draft.campaignBeingEditedOffer,
          action.payload.key,
          action.payload.tierIndex,
          action.payload.value
        );
      } else if (draft.offers) {
        const index = getOfferIndexByVariationId(
          draft.offers,
          action.payload.variationId
        );

        draft.offers[index] = setUnit(
          draft.offers[index],
          action.payload.key,
          action.payload.tierIndex,
          action.payload.value
        );
      }
      return draft;
    },
    setOfferMessage: (
      draft,
      action: PayloadAction<{
        tierIndex: number;
        value: string;
        variationId: string;
        isCampaignBeingEdited: boolean;
      }>
    ) => {
      if (
        action.payload.isCampaignBeingEdited &&
        draft.campaignBeingEditedOffer
      ) {
        draft.campaignBeingEditedOffer.tiers[
          action.payload.tierIndex
        ].discountTitle = action.payload.value;
      } else if (draft.offers) {
        const index = getOfferIndexByVariationId(
          draft.offers,
          action.payload.variationId
        );

        draft.offers[index].tiers[action.payload.tierIndex].discountTitle =
          action.payload.value;
      }
      return draft;
    },
    toggleOfferType: (
      draft,
      action: PayloadAction<{
        key: DiscountSettingFieldType;
        value: string;
        variationId: string;
        isCampaignBeingEdited: boolean;
      }>
    ) => {
      if (
        action.payload.isCampaignBeingEdited &&
        draft.campaignBeingEditedOffer
      ) {
        if (action.payload.key === "unitType") {
          draft.campaignBeingEditedOffer = toggleDiscountUnitType(
            draft.campaignBeingEditedOffer,
            action.payload
          );
        } else {
          draft.campaignBeingEditedOffer.discountType = action.payload
            .value as DiscountDiscountType;
          if (action.payload.value === "percentage") {
            draft.campaignBeingEditedOffer.discountType = action.payload
              .value as DiscountDiscountType;
          } else {
            draft.campaignBeingEditedOffer.discountType = "dollar";
            if (action.payload.value === "dollarPerOrder") {
              draft.campaignBeingEditedOffer.discountApplicationMethod =
                "perOrder";
            } else {
              draft.campaignBeingEditedOffer.discountApplicationMethod =
                "perItem";
            }
          }
        }
      } else if (draft.offers) {
        const index = getOfferIndexByVariationId(
          draft.offers,
          action.payload.variationId
        );

        if (action.payload.key === "unitType") {
          draft.offers[index] = toggleDiscountUnitType(
            draft.offers[index],
            action.payload
          );
        } else {
          draft.offers[index].discountType = action.payload
            .value as DiscountDiscountType;
          if (action.payload.value === "percentage") {
            draft.offers[index].discountType = action.payload
              .value as DiscountDiscountType;
          } else {
            draft.offers[index].discountType = "dollar";
            if (action.payload.value === "dollarPerOrder") {
              draft.offers[index].discountApplicationMethod = "perOrder";
            } else {
              draft.offers[index].discountApplicationMethod = "perItem";
            }
          }
        }
      }
      return draft;
    },
    toggleFreeShipping: (
      draft,
      action: PayloadAction<{
        tierIndex: number;
        variationId: string;
        isTestFlow: boolean;
      }>
    ) => {
      if (action.payload.isTestFlow && draft.campaignBeingEditedOffer) {
        draft.campaignBeingEditedOffer.tiers[
          action.payload.tierIndex
        ].isFreeShipping =
          !draft.campaignBeingEditedOffer.tiers[action.payload.tierIndex]
            .isFreeShipping;
      } else if (draft.offers) {
        const index = getOfferIndexByVariationId(
          draft.offers,
          action.payload.variationId
        );

        draft.offers[index].tiers[action.payload.tierIndex].isFreeShipping =
          !draft.offers[index].tiers[action.payload.tierIndex].isFreeShipping;
      }
      return draft;
    },
    toggleAutoAddGWP: (
      draft,
      action: PayloadAction<{
        tierIndex: number;
        value: boolean;
        variationId: string;
        isTestFlow: boolean;
      }>
    ) => {
      if (draft.campaignBeingEditedOffer && action.payload.isTestFlow) {
        draft.campaignBeingEditedOffer.tiers[
          action.payload.tierIndex
        ].autoAddGiftWithPurchase =
          action.payload.value &&
          draft.campaignBeingEditedOffer.tiers[action.payload.tierIndex]
            .giftWithPurchaseVariantId !== null;
      } else if (draft.offers) {
        const index = getOfferIndexByVariationId(
          draft.offers,
          action.payload.variationId
        );

        draft.offers[index].tiers[
          action.payload.tierIndex
        ].autoAddGiftWithPurchase =
          action.payload.value &&
          draft.offers[index].tiers[action.payload.tierIndex]
            .giftWithPurchaseVariantId !== null;
      }
      return draft;
    },
    toggleGiftWithPurchase: (
      draft,
      action: PayloadAction<{
        tierIndex: number;
        value: boolean;
        variationId: string;
        isTestFlow: boolean;
      }>
    ) => {
      if (draft.campaignBeingEditedOffer && action.payload.isTestFlow) {
        draft.campaignBeingEditedOffer.tiers[
          action.payload.tierIndex
        ].isGiftWithPurchase = action.payload.value;
      } else if (draft.offers) {
        const index = getOfferIndexByVariationId(
          draft.offers,
          action.payload.variationId
        );

        draft.offers[index].tiers[action.payload.tierIndex].isGiftWithPurchase =
          action.payload.value;
      }
      return draft;
    },
    toggleShouldStack: (
      draft,
      action: PayloadAction<{
        value: boolean;
        variationId: string;
        isTestFlow: boolean;
      }>
    ) => {
      if (draft.campaignBeingEditedOffer && action.payload.isTestFlow) {
        draft.campaignBeingEditedOffer.shouldStack = action.payload.value;
      } else if (draft.offers) {
        const index = getOfferIndexByVariationId(
          draft.offers,
          action.payload.variationId
        );

        draft.offers[index].shouldStack = action.payload.value;
      }
      return draft;
    },
    toggleShouldRejectCodes: (
      draft,
      action: PayloadAction<{
        value: boolean;
        variationId: string;
        isTestFlow: boolean;
      }>
    ) => {
      if (draft.campaignBeingEditedOffer && action.payload.isTestFlow) {
        draft.campaignBeingEditedOffer.shouldRejectCodes = action.payload.value;
      } else if (draft.offers) {
        const index = getOfferIndexByVariationId(
          draft.offers,
          action.payload.variationId
        );

        draft.offers[index].shouldRejectCodes = action.payload.value;
      }
      return draft;
    },
    setOfferApplicationType: (
      draft,
      action: PayloadAction<{
        discountApplicationType: DiscountApplicationType;
        variationId: string;
        isTestFlow: boolean;
      }>
    ) => {
      if (action.payload.isTestFlow) {
        if (!draft.campaignBeingEditedOffer) {
          draft.campaignBeingEditedOffer = initialOfferState({
            variationId: action.payload.variationId,
          });

          draft.campaignBeingEditedOffer.discountApplicationType =
            action.payload.discountApplicationType;
        } else {
          draft.campaignBeingEditedOffer.discountApplicationType =
            action.payload.discountApplicationType;
        }
      } else if (draft.offers.length === 0) {
        draft.offers.push(
          initialOfferState({ variationId: action.payload.variationId })
        );
        draft.offers[0].discountApplicationType =
          action.payload.discountApplicationType;
      } else {
        const index = getOfferIndexByVariationId(
          draft.offers,
          action.payload.variationId
        );
        draft.offers[index].discountApplicationType =
          action.payload.discountApplicationType;
      }
      return draft;
    },

    setOfferGwp: (
      draft,
      action: PayloadAction<{
        tierIndex: number;
        productId: string | null;
        variantId: string | null;
        handle: string | null;
        variationId: string;
        isTestFlow: boolean;
      }>
    ) => {
      if (action.payload.isTestFlow && draft.campaignBeingEditedOffer) {
        draft.campaignBeingEditedOffer.tiers[
          action.payload.tierIndex
        ].giftWithPurchaseVariantId = action.payload.variantId
          ? fromGqlId(action.payload.variantId)
          : null;
        draft.campaignBeingEditedOffer.tiers[
          action.payload.tierIndex
        ].giftWithPurchaseProductId = action.payload.productId
          ? fromGqlId(action.payload.productId)
          : null;
        draft.campaignBeingEditedOffer.tiers[
          action.payload.tierIndex
        ].autoAddGiftWithPurchase = action.payload.variantId !== null;
        draft.campaignBeingEditedOffer.tiers[
          action.payload.tierIndex
        ].giftWithPurchaseHandle = action.payload.handle;
      } else if (draft.offers) {
        const index = getOfferIndexByVariationId(
          draft.offers,
          action.payload.variationId
        );
        draft.offers[index].tiers[
          action.payload.tierIndex
        ].giftWithPurchaseVariantId = action.payload.variantId
          ? fromGqlId(action.payload.variantId)
          : null;
        draft.offers[index].tiers[
          action.payload.tierIndex
        ].giftWithPurchaseProductId = action.payload.productId
          ? fromGqlId(action.payload.productId)
          : null;
        draft.offers[index].tiers[
          action.payload.tierIndex
        ].autoAddGiftWithPurchase = action.payload.variantId !== null;
        draft.offers[index].tiers[
          action.payload.tierIndex
        ].giftWithPurchaseHandle = action.payload.handle;
      }
      return draft;
    },
    removeOfferGwp: (
      draft,
      action: PayloadAction<{
        tierIndex: number;
        variationId: string;
        isTestFlow: boolean;
      }>
    ) => {
      if (action.payload.isTestFlow && draft.campaignBeingEditedOffer) {
        draft.campaignBeingEditedOffer.tiers[
          action.payload.tierIndex
        ].giftWithPurchaseVariantId = undefined;
        draft.campaignBeingEditedOffer.tiers[
          action.payload.tierIndex
        ].giftWithPurchaseProductId = undefined;
        draft.campaignBeingEditedOffer.tiers[
          action.payload.tierIndex
        ].giftWithPurchaseHandle = undefined;
      } else if (draft.offers) {
        const index = getOfferIndexByVariationId(
          draft.offers,
          action.payload.variationId
        );
        draft.offers[index].tiers[
          action.payload.tierIndex
        ].giftWithPurchaseVariantId = undefined;
        draft.offers[index].tiers[
          action.payload.tierIndex
        ].giftWithPurchaseProductId = undefined;
        draft.offers[index].tiers[
          action.payload.tierIndex
        ].giftWithPurchaseHandle = undefined;
      }
      return draft;
    },
    setMaximumOfferAmount: (
      draft,
      action: PayloadAction<{
        maximumDiscountAmount: number | undefined;
        variationId: string;
        isTestFlow: boolean;
      }>
    ) => {
      if (action.payload.isTestFlow && draft.campaignBeingEditedOffer) {
        draft.campaignBeingEditedOffer.maximumDollarDiscount =
          action.payload.maximumDiscountAmount;
      } else if (draft.offers) {
        const index = getOfferIndexByVariationId(
          draft.offers,
          action.payload.variationId
        );
        draft.offers[index].maximumDollarDiscount =
          action.payload.maximumDiscountAmount;
      }
      return draft;
    },
  },
  selectors: {
    selectedOffers: (state) => state.offers as OfferDto[],
    selectedCampaignSingleOffer: (state) => state.offers[0] as OfferDto,
    selectedOfferState: (state) => state,
    selectedStoredOffer: (state) => state.storedOffers,
  },
});

export const {
  resetOffer,
  toggleGiftWithPurchase,
  toggleFreeShipping,
  toggleOfferType,
  switchToCartDiscountType,
  toggleShouldStack,
  removeOfferTier,
  setOfferUnit,
  setOfferMessage,
  setOfferApplicationType,
  removeOfferGwp,
  setOfferGwp,
  toggleShouldRejectCodes,
  setMaximumOfferAmount,
  toggleAutoAddGWP,
  addOfferTier,
  //NEW
  initOffersFromVariations,
  initPersonalizationOffer,
  initCampaignCreateOffer,
  setCampaignBeingEditedOffer,
  resetCampaignCreateOffer,
  resetOfferToStored,
} = offersSlice.actions;

export const offersReducer = offersSlice.reducer;
export const {
  selectedOffers,
  selectedStoredOffer,
  selectedOfferState,
  selectedCampaignSingleOffer,
} = offersSlice.selectors;
